// The camera lab (gate A2.4, `?scene=camera`): a flat yard with the obstacles a third-person camera meets (a long
// wall with an end to turn around, a 0.8 m pillar, a 0.12 m pole, an overhang on four thin posts, a fadeable bush)
// and a scripted target that runs a route through them at run speed. The controller stream builds the hero at the
// same time, so the camera is tested on this stand-in; the target is a `SteppedTarget` (position and velocity pushed
// each sim step, sampled at the frame's alpha), the interface the Wanderer will speak at the merge.
//
// `__rpg.camera` drives it for the specs: `run(script)` plays the route on scripted frame times (60 Hz, 144 Hz,
// 60 Hz ± 2 ms) through the runtime's real frame path and reads the camera every frame against independent checks
// (a ball query at the camera, the distance to the target's capsule); `probeArm()` measures the arm's step response
// at a wall; `jitter(pattern)` measures the target's screen jitter on a straight run.
import * as THREE from "three";
import { createCollisionWorld, type BodySpec, type CollisionWorld, type Rapier, type Vec3 } from "@rpg/collision";
import { SteppedTarget, forwardOf, frameTimes, jitterOf, nearCornerDistance, yawOf, type FramePattern, type JitterReport, type JitterSample } from "@rpg/camera";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import { mountCamera, pointerLockOnPress, type LookSource, type SpikeCamera } from "./index";

/** The lab target's capsule (m): the Wanderer's size, near enough. */
export const TARGET = { radius: 0.35, halfHeight: 0.55 } as const;
/** Run speed along the routes (m/s): the tuning table's run speed. */
const RUN = 5.5;

/** A route: waypoints on the ground (x, z), with optional stops (s) at a waypoint. */
interface Route {
  points: { x: number; z: number; stop?: number }[];
}

/** The routes. `tour` passes every obstacle; `straight` is an open run for the jitter measurement. */
export const ROUTES: Record<string, Route> = {
  tour: {
    points: [
      { x: 0, z: -16 },
      { x: 0, z: -12.2 },
      { x: 0.75, z: -11 }, // swerve round the pole, then back onto its line: the pole sits on the view line
      { x: 0, z: -9.8 },
      { x: 0, z: -4 }, // along the wall (left)
      { x: -3, z: -4 },
      { x: -5, z: -5.8 }, // swerve round the pillar, then back onto its line
      { x: -7, z: -4 },
      { x: -12, z: -4, stop: 1 },
      { x: -12, z: 7 }, // under the overhang
      { x: -9, z: 7 },
      { x: -7.5, z: 5.4 }, // swerve round the bush, then back onto its line
      { x: -5.5, z: 7 },
      { x: 2.4, z: 7 },
      { x: 2.4, z: -16 }, // down the wall's far side, close to it
      { x: -3, z: -16 }, // round the wall's end
    ],
  },
  straight: { points: [{ x: -30, z: 24 }, { x: 30, z: 24 }] },
};

/** The lab's static bodies (fixed, layer "world"); `thin` marks the ones under 0.3 m, `fade` the fadeable ones. */
function labBodies(): (BodySpec & { colour: string; thin?: boolean; fade?: boolean })[] {
  const box = (id: string, x: number, y: number, z: number, hx: number, hy: number, hz: number, colour: string, tag?: string) => ({
    id,
    kind: "fixed" as const,
    position: { x, y, z },
    colliders: [{ shape: { type: "cuboid" as const, halfExtents: { x: hx, y: hy, z: hz } }, layer: "world", ...(tag ? { tag } : {}) }],
    colour,
  });
  const post = (id: string, x: number, z: number, h: number, r: number) => ({
    id,
    kind: "fixed" as const,
    position: { x, y: h / 2, z },
    colliders: [{ shape: { type: "cylinder" as const, halfHeight: h / 2, radius: r }, layer: "world" }],
    colour: "#6f6a62",
    thin: true,
  });
  return [
    box("ground", 0, -0.25, 0, 40, 0.25, 40, "#8fb36a", "grass"),
    box("wall", 1.6, 1.5, -8.5, 0.2, 1.5, 5.5, "#b9a98f", "stone"),
    box("pillar", -5, 1.5, -4, 0.4, 1.5, 0.4, "#a39377", "stone"),
    post("pole", 0, -11, 3, 0.06),
    box("roof", -12, 2.5, 3.5, 2, 0.125, 2, "#9c6b4e", "wood"),
    post("post-a", -13.85, 1.65, 2.375, 0.075),
    post("post-b", -10.15, 1.65, 2.375, 0.075),
    post("post-c", -13.85, 5.35, 2.375, 0.075),
    post("post-d", -10.15, 5.35, 2.375, 0.075),
    { ...box("bush", -7.5, 1.2, 7, 0.5, 1.2, 0.5, "#4f7d3c", "foliage"), fade: true },
  ];
}

/** Where a route puts the target at time t: position, velocity, facing. Loops when `loop` is set. */
function routeAt(route: Route, t: number, loop: boolean): { p: Vec3; v: Vec3; facing: number } {
  const pts = route.points;
  const legs: { a: (typeof pts)[number]; b: (typeof pts)[number]; len: number; dur: number; stop: number }[] = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    legs.push({ a, b, len, dur: len / RUN, stop: b.stop ?? 0 });
  }
  const total = legs.reduce((s, l) => s + l.dur + l.stop, 0);
  let left = loop ? t % total : Math.min(t, total);
  for (const l of legs) {
    const facing = yawOf(l.b.x - l.a.x, l.b.z - l.a.z);
    if (left <= l.dur) {
      const f = l.dur > 0 ? left / l.dur : 1;
      const d = forwardOf(facing);
      return { p: { x: l.a.x + (l.b.x - l.a.x) * f, y: 0, z: l.a.z + (l.b.z - l.a.z) * f }, v: { x: d.x * RUN, y: 0, z: d.z * RUN }, facing };
    }
    left -= l.dur;
    if (left <= l.stop) return { p: { x: l.b.x, y: 0, z: l.b.z }, v: { x: 0, y: 0, z: 0 }, facing };
    left -= l.stop;
  }
  const last = legs[legs.length - 1]!;
  return { p: { x: last.b.x, y: 0, z: last.b.z }, v: { x: 0, y: 0, z: 0 }, facing: yawOf(last.b.x - last.a.x, last.b.z - last.a.z) };
}

/** The route's length in seconds. */
function routeSeconds(route: Route): number {
  let s = 0;
  for (let i = 0; i + 1 < route.points.length; i++) {
    const a = route.points[i]!;
    const b = route.points[i + 1]!;
    s += Math.hypot(b.x - a.x, b.z - a.z) / RUN + (b.stop ?? 0);
  }
  return s;
}

/** A scripted run: the route, the frame pattern, and look and profile events by time (s). */
export interface RunScript {
  route?: keyof typeof ROUTES;
  pattern?: FramePattern;
  /** Seconds to run (default the route's length plus 1 s). */
  seconds?: number;
  /** Look input (rad), spread over `over` seconds from `t` (default 0.3 s: a quick mouse swing). */
  look?: { t: number; dx: number; dy: number; over?: number }[];
  profiles?: ({ t: number; request: string; layer?: string; blend?: number } | { t: number; release: string; blend?: number })[];
  /** Hold the drift back off (a tiny look input every frame), for a still view. */
  holdView?: boolean;
  /** Keep every frame's record in the result (large); default only the summary and the per-frame series it needs. */
  frames?: boolean;
}

/** One frame of a run, read from the camera and checked independently of the rig. */
export interface RunFrame {
  t: number;
  dt: number;
  steps: number;
  arm: number;
  want: number;
  allowed: number;
  cam: Vec3;
  /** The point the camera looks at. */
  pivot: Vec3;
  /** Bodies a ball of the near plane's corner distance at the camera overlaps (should stay empty). */
  inside: string[];
  /** The camera's distance from the target's capsule surface (m; < 0 is inside the target). */
  targetGap: number;
  blocker: string | null;
  thin: string[];
  faded: string[];
  /** A thin body lies across the view line (an independent ball cast from the pivot to the camera, thin bodies only). */
  thinAcross: string[];
  /** The rig cut this frame (a forced cut through a looked-past body). */
  cut: boolean;
  profile: string;
  blend: number;
}

/** What a run found. */
export interface RunResult {
  frames: number;
  seconds: number;
  /** Frames with the camera inside a collider, and inside (or touching) the target. */
  insideFrames: number;
  insideBodies: string[];
  targetFrames: number;
  minTargetGap: number;
  /** The largest arm change in one frame (m). */
  maxArmStep: number;
  /** Frames that cut (left out of maxArmStep). */
  cuts: number;
  /** The fastest pull-in and ease-out seen (m/s). */
  maxInSpeed: number;
  maxOutSpeed: number;
  /** Frames with a thin body across the view line; frames where a thin body stopped the arm. */
  thinAcrossFrames: number;
  thinBlockedFrames: number;
  /** Frames where the arm looked past a thin body; frames with an occluder fading. */
  thinPastFrames: number;
  fadedFrames: number;
  /** Frames the arm was shorter than wanted (something in the way). */
  pulledFrames: number;
  /** Each blend: the profile, its set time, and the frames and seconds it took to settle. */
  blends: { profile: string; set: number; frames: number; seconds: number; frameDt: number }[];
  /** Casts per frame (mean, max). */
  castsMean: number;
  castsMax: number;
  frameRecords?: RunFrame[];
}

/** The lab's handle at `__rpg.camera`. */
export interface CameraLabHandle {
  /** The rig's last frame and its profile state. */
  state(): ReturnType<CameraLab["state"]>;
  request(name: string, o?: { layer?: string; blend?: number }): void;
  release(layer: string, o?: { blend?: number }): void;
  /** Look input as a player would give it (rad). */
  look(dx: number, dy: number): void;
  /** Play a scripted run and return what the checks found. */
  run(script?: RunScript): RunResult;
  /** The arm's step response at the wall: frames to 90 % pulling in and easing out, at 60 Hz. */
  probeArm(): { full: number; short: number; inFrames: number; outFrames: number };
  /** The target's screen jitter on a straight run at a frame pattern. */
  jitter(pattern: FramePattern): JitterReport;
  /** The profile names. */
  profiles(): string[];
}

/** The mounted lab. */
export interface CameraLab {
  handle: CameraLabHandle;
  camera: SpikeCamera;
  world: CollisionWorld;
  state(): { profile: string; blend: number; yaw: number; pitch: number; arm: number; want: number; allowed: number; routeTime: number; position: Vec3 };
}

/** Mount the lab when `?scene=camera` is set; null otherwise. */
export function mountCameraLab(o: { rapier: Rapier; runtime: Runtime; scene: THREE.Scene; camera: THREE.PerspectiveCamera; content: SpikeContent; el?: HTMLElement; search?: string; look?: LookSource; setting?: (id: string) => unknown }): CameraLab | null {
  const q = new URLSearchParams(o.search ?? location.search);
  if (q.get("scene") !== "camera") return null;
  const { runtime, scene, camera } = o;

  // the world: its own collision world (the A1 sim's hash and saves stay as they were)
  const world = createCollisionWorld(o.rapier, { dt: runtime.clock.dt, layers: { world: [], hero: [] } });
  const bodies = labBodies();
  for (const b of bodies) world.add(b);
  world.add({ id: "hero", kind: "kinematic", position: { x: 0, y: TARGET.halfHeight + TARGET.radius, z: 0 }, colliders: [{ shape: { type: "capsule", ...TARGET }, layer: "hero" }] });
  world.step(); // the broad phase sees the fixed bodies from here on
  const thinIds = bodies.filter((b) => b.thin).map((b) => b.id);

  // the view: the A1 pen hidden, the lab's meshes, the target's capsule
  const pen = scene.getObjectByName("sim-test-scene");
  if (pen) pen.visible = false;
  scene.background = new THREE.Color("#cfe3c0");
  const group = new THREE.Group();
  group.name = "camera-lab";
  scene.add(group);
  const sun = new THREE.DirectionalLight("#fff1d0", 1.5);
  sun.position.set(10, 18, 6);
  group.add(sun);
  const meshes = new Map<string, THREE.Mesh>();
  for (const b of bodies) {
    const s = b.colliders[0]!.shape;
    const geo =
      s.type === "cuboid"
        ? new THREE.BoxGeometry(s.halfExtents.x * 2, s.halfExtents.y * 2, s.halfExtents.z * 2)
        : s.type === "cylinder"
          ? new THREE.CylinderGeometry(s.radius, s.radius, s.halfHeight * 2, 10)
          : new THREE.BoxGeometry(1, 1, 1);
    // every lab material is transparent from the start, so a fade never compiles a new shader after ready
    const mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: b.colour, flatShading: true, transparent: true }));
    mesh.position.set(b.position!.x, b.position!.y, b.position!.z);
    group.add(mesh);
    meshes.set(b.id, mesh);
  }
  const heroMat = new THREE.MeshLambertMaterial({ color: "#ad2c25", flatShading: true, transparent: true });
  const hero = new THREE.Mesh(new THREE.CapsuleGeometry(TARGET.radius, TARGET.halfHeight * 2, 3, 10), heroMat);
  const nose = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 0.3), heroMat);
  nose.position.set(0, 0.45, TARGET.radius);
  hero.add(nose);
  group.add(hero);

  // the target: the route, stepped in the sim phase; the camera samples it between steps
  let route: Route = ROUTES.tour!;
  let loop = true;
  let routeTime = 0;
  const at0 = routeAt(route, 0, loop);
  const target = new SteppedTarget({ position: at0.p, velocity: at0.v, facing: at0.facing });
  const place = (t: number, teleport: boolean): void => {
    const r = routeAt(route, t, loop);
    const s = { position: r.p, velocity: r.v, facing: r.facing, grounded: true };
    if (teleport) target.teleport(s);
    else target.push(s);
    world.setPose("hero", { x: r.p.x, y: r.p.y + TARGET.halfHeight + TARGET.radius, z: r.p.z });
  };
  runtime.on(
    "sim",
    (c) => {
      routeTime += c.dt;
      place(routeTime, false);
    },
    { name: "camera lab: route" },
  );

  const values = o.content.registry.get("camera-rig", "default");
  const el = o.el ?? (document.querySelector("canvas") as HTMLElement | null) ?? document.body;
  pointerLockOnPress(el);
  const cam = mountCamera({
    runtime,
    camera,
    target,
    content: o.content,
    collision: { world, layers: ["world"], exclude: "hero", canFade: (_id, tag) => tag === "foliage" },
    yaw: at0.facing,
    ...(o.look ? { look: o.look } : {}),
    ...(o.setting ? { setting: o.setting } : {}),
  });
  const rig = cam.rig;

  // the drawn target and the fades, after the rig (the same alpha)
  runtime.on(
    "late",
    (c) => {
      const s = target.sample(c.alpha);
      hero.position.set(s.position.x, s.position.y + TARGET.halfHeight + TARGET.radius, s.position.z);
      hero.rotation.y = s.facing ?? 0;
      heroMat.opacity = rig.frame().targetOpacity;
      for (const [id, m] of meshes) (m.material as THREE.MeshLambertMaterial).opacity = rig.fades().get(id) ?? 1;
    },
    { name: "camera lab: view" },
  );

  // ── the checks' runner ──────────────────────────────────────────────────────────────────────────
  const ID = { x: 0, y: 0, z: 0, w: 1 };
  const capsuleGap = (p: Vec3, feet: Vec3): number => {
    const y0 = feet.y + TARGET.radius;
    const y1 = feet.y + TARGET.radius + TARGET.halfHeight * 2;
    const cy = Math.min(y1, Math.max(y0, p.y));
    return Math.hypot(p.x - feet.x, p.y - cy, p.z - feet.z) - TARGET.radius;
  };
  const restart = (name: keyof typeof ROUTES, loopIt: boolean): void => {
    route = ROUTES[name]!;
    loop = loopIt;
    routeTime = 0;
    place(0, true);
    rig.yaw = routeAt(route, 0, loop).facing;
    rig.snap();
  };
  /** Run frames with the clock unpaused and no drawing; restores the pause after. */
  const withFrames = <T>(fn: (frame: (dt: number) => ReturnType<Runtime["frame"]>) => T): T => {
    const paused = runtime.paused;
    runtime.paused = false;
    try {
      return fn((dt) => runtime.frame(dt, { draw: false }));
    } finally {
      runtime.paused = paused;
    }
  };

  const run = (script: RunScript = {}): RunResult => {
    const name = script.route ?? "tour";
    const pattern = script.pattern ?? "60";
    const hz = pattern === "144" ? 144 : 60;
    const seconds = script.seconds ?? routeSeconds(ROUTES[name]!) + 1;
    const n = Math.round(seconds * hz);
    const dts = frameTimes(pattern, n, 11);
    const looks = script.look ?? [];
    const events = [...(script.profiles ?? [])].sort((a, b) => a.t - b.t);
    const out: RunFrame[] = [];
    const blends: RunResult["blends"] = [];
    let pending: { profile: string; set: number; frames: number; seconds: number; frameDt: number } | null = null;
    let castsSum = 0;
    let castsMax = 0;
    cam.request(o.content.registry.get("camera-rig", "default")?.initial ?? "explore", { layer: "mode", blend: 0 });
    for (const l of ["context", "overlay"]) cam.release(l, { blend: 0 });
    restart(name, false);
    withFrames((frame) => {
      let t = 0;
      let ei = 0;
      for (let i = 0; i < n; i++) {
        const dt = dts[i]!;
        // look input as a player turns: each swing spread over its time (a mouse flick or a stick push), this frame's share
        for (const l of looks) {
          const over = l.over ?? 0.3;
          const share = over > 0 ? Math.max(0, Math.min(t + dt, l.t + over) - Math.max(t, l.t)) / over : l.t >= t && l.t < t + dt ? 1 : 0;
          if (share > 0) rig.look(l.dx * share, l.dy * share);
        }
        if (script.holdView) rig.look(1e-9, 0);
        while (ei < events.length && events[ei]!.t <= t) {
          const e = events[ei]!;
          if ("request" in e) cam.request(e.request, { ...(e.layer ? { layer: e.layer } : {}), ...(e.blend !== undefined ? { blend: e.blend } : {}) });
          else cam.release(e.release, e.blend !== undefined ? { blend: e.blend } : {});
          const b = rig.profiles.state();
          if (b.duration > 0 && b.progress < 1) {
            if (pending) blends.push(pending); // retargeted mid-blend: recorded as unfinished
            pending = { profile: b.active, set: b.duration, frames: 0, seconds: 0, frameDt: 0 };
          }
          ei++;
        }
        const tick = frame(dt);
        t += dt;
        const f = rig.frame();
        castsSum += f.casts;
        castsMax = Math.max(castsMax, f.casts);
        const b = rig.profiles.state();
        if (pending) {
          pending.frames++;
          pending.seconds += dt;
          pending.frameDt = Math.max(pending.frameDt, dt);
          if (b.progress >= 1) {
            blends.push(pending);
            pending = null;
          }
        }
        const feet = target.sample(tick.alpha).position;
        const r = nearCornerDistance(camera.near, camera.fov, camera.aspect);
        const inside = world.overlap({ type: "ball", radius: r }, f.position, ID, { layers: ["world"], exclude: "hero" });
        const dir = { x: f.position.x - f.pivot.x, y: f.position.y - f.pivot.y, z: f.position.z - f.pivot.z };
        const len = Math.hypot(dir.x, dir.y, dir.z);
        const thinAcross: string[] = [];
        if (len > 1e-6) {
          for (const id of thinIds) {
            const h = world.castShape({ type: "ball", radius: r + (values?.probeMargin ?? 0) }, f.pivot, ID, dir, len, { filter: (x) => x === id });
            if (h) thinAcross.push(id);
          }
        }
        out.push({
          t,
          dt,
          steps: tick.steps,
          arm: f.arm,
          want: f.want,
          allowed: f.allowed,
          cam: f.position,
          pivot: f.pivot,
          inside,
          targetGap: capsuleGap(f.position, feet),
          blocker: f.blocker,
          thin: f.thin,
          faded: f.faded,
          thinAcross,
          profile: b.active,
          blend: b.progress,
          cut: f.cut,
        });
      }
    });
    const unfinished = pending as RunResult["blends"][number] | null; // (set inside the frame loop)
    if (unfinished) blends.push({ ...unfinished, frames: -1 });
    let maxArmStep = 0;
    let maxIn = 0;
    let maxOut = 0;
    for (let i = 1; i < out.length; i++) {
      if (out[i]!.cut) continue; // a cut is a deliberate jump, counted apart
      const d = out[i]!.arm - out[i - 1]!.arm;
      maxArmStep = Math.max(maxArmStep, Math.abs(d));
      if (d < 0) maxIn = Math.max(maxIn, -d / out[i]!.dt);
      else maxOut = Math.max(maxOut, d / out[i]!.dt);
    }
    const insideBodies = [...new Set(out.flatMap((f) => f.inside))];
    return {
      frames: out.length,
      seconds: out.reduce((s, f) => s + f.dt, 0),
      insideFrames: out.filter((f) => f.inside.length > 0).length,
      insideBodies,
      targetFrames: out.filter((f) => f.targetGap <= 0).length,
      minTargetGap: Math.min(...out.map((f) => f.targetGap)),
      maxArmStep,
      cuts: out.filter((f) => f.cut).length,
      maxInSpeed: maxIn,
      maxOutSpeed: maxOut,
      thinAcrossFrames: out.filter((f) => f.thinAcross.length > 0).length,
      thinBlockedFrames: out.filter((f) => f.blocker !== null && thinIds.includes(f.blocker)).length,
      thinPastFrames: out.filter((f) => f.thin.length > 0).length,
      fadedFrames: out.filter((f) => f.faded.length > 0).length,
      pulledFrames: out.filter((f) => f.arm < f.want - 0.05).length,
      blends,
      castsMean: castsSum / Math.max(1, out.length),
      castsMax,
      ...(script.frames ? { frameRecords: out } : {}),
    };
  };

  const probeArm = (): { full: number; short: number; inFrames: number; outFrames: number } => {
    // stand 2.4 m from the wall's face, look along the wall's normal, then turn to put the arm into the wall
    restart("straight", false);
    route = { points: [{ x: -1.2, z: -8 }, { x: -1.2, z: -8, stop: 1e6 }] }; // standing still
    place(0, true);
    const away = Math.PI / 2; // looking +x towards the wall: the camera sits at -x, in the open
    const into = -Math.PI / 2; // looking -x: the camera sits at +x, behind the wall
    rig.yaw = away;
    rig.snap();
    return withFrames((frame) => {
      const settle = (k: number): void => {
        for (let i = 0; i < k; i++) {
          rig.look(1e-9, 0);
          frame(1 / 60);
        }
      };
      const t90 = (from: number, to: number): number => {
        for (let i = 1; i < 600; i++) {
          rig.look(1e-9, 0);
          frame(1 / 60);
          if (Math.abs(rig.frame().arm - from) >= 0.9 * Math.abs(to - from)) return i;
        }
        return Infinity;
      };
      rig.yaw = into;
      settle(240);
      const short = rig.frame().arm;
      rig.yaw = away;
      settle(300);
      const full = rig.frame().arm;
      rig.yaw = into;
      const inFrames = t90(full, short);
      settle(120);
      rig.yaw = away;
      const outFrames = t90(rig.frame().arm, full);
      return { full, short, inFrames, outFrames };
    });
  };

  const jitter = (pattern: FramePattern): JitterReport => {
    restart("straight", false);
    const behind = routeAt(route, 0, false).facing;
    rig.yaw = behind + 0.7; // 40° off the back: judder shows sideways on screen
    rig.snap();
    const hz = pattern === "144" ? 144 : 60;
    const v = new THREE.Vector3();
    return withFrames((frame) => {
      const samples: JitterSample[] = [];
      const dts = frameTimes(pattern, Math.round(7 * hz), 5);
      dts.forEach((dt, i) => {
        rig.look(1e-9, 0); // the player holds the view: no drift back
        const tick = frame(dt);
        if (i < 4 * hz) return; // the follow and the look-ahead settle
        const s = target.sample(tick.alpha).position;
        v.set(s.x, s.y + 1, s.z).project(camera);
        samples.push({ dt, screen: { x: v.x * 960, y: v.y * 540 }, camera: { x: camera.position.x, y: camera.position.y, z: camera.position.z } });
      });
      return jitterOf(samples);
    });
  };

  const state = () => {
    const f = rig.frame();
    const b = rig.profiles.state();
    return { profile: b.active, blend: b.progress, yaw: f.yaw, pitch: f.pitch, arm: f.arm, want: f.want, allowed: f.allowed, routeTime, position: f.position };
  };

  return {
    camera: cam,
    world,
    state,
    handle: {
      state,
      request: (name, b) => cam.request(name, b),
      release: (layer, b) => cam.release(layer, b),
      look: (dx, dy) => rig.look(dx, dy),
      run,
      probeArm,
      jitter,
      profiles: () => rig.profiles.names(),
    },
  };
}
